import { useMemo } from "react";
import { TableColumn } from "@components/Table/TableColumns";
import { getFormattedThousand, getRem, ID, markMatch } from "@datassential/platform-ui-lib";
import { HeatScore } from "@models/HeatScore";
import { TrendSummaryItem, TrendSummaryResponse } from "@api/useTrendSummary";
import MacStageLabel from "@vms/MacStageLabel";
import HeatScoreLabel from "@vms/HeatScoreLabel";
import { useTrendContext } from "@pages/Trend/TrendContext";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useGetMetric } from "@core/CommonDataContext";
import { Metric } from "@models/Metric";

const COLUMN_WIDTH = 70;

export function useTrendSummaryTableConfig(
    data: TrendSummaryResponse,
    heatScoreTooltipId: ID,
    hideSorting: boolean = false,
) {
    const { search } = useTrendContext();

    const getMetric = useGetMetric();
    const metrics = useMemo(() => {
        let metrics = data[0]?.metrics ?? [];

        if (!metrics.length) {
            const knowItMetric = getMetric(Metric.KnowIt);
            const haveTriedItMetric = getMetric(Metric.HaveTriedIt);
            const loveItMetric = getMetric(Metric.LoveIt);
            const loveLikeItMetric = getMetric(Metric.LoveLikeIt);
            /* todo: investigate why it's broken */
            metrics = [knowItMetric, haveTriedItMetric, loveItMetric, loveLikeItMetric] as unknown as TrendSummaryItem['metrics'];
        }

        return metrics;
    }, [data, getMetric])

    return useMemo(
        (): TableColumn[] => [
            {
                key: 'name',
                sortKey: hideSorting ? undefined : 'name',
                head: 'Name',
                headText: 'Name',
                Value: ({ index }) => search.trim().length > 0 ? (
                    <span dangerouslySetInnerHTML={{
                        __html: markMatch(data[index]?.name ?? '', search, true)
                    }}/>
                ) : <>{data[index]?.name}</>,
                valueText: (index) => `${data[index]?.name}`,
                style: {
                    cell: {
                        flex: `1 0 ${getRem(300)}`,
                        paddingLeft: getRem(16),
                        position: "sticky",
                        left: 0,
                    },
                    head: {
                        zIndex: 3,
                    },
                },
            },
            {
                key: 'macStage',
                sortKey: hideSorting ? undefined : 'macStage',
                head: 'MAC Stage',
                headText: 'MAC Stage',
                Value: ({ index }) => data[index]?.macStage ? (
                    <MacStageLabel
                        macStageId={data[index]?.macStage}
                        modifiers={["medium"]}
                    />
                ) : <>-</>,
                valueText: (index) => `${data[index]?.macStageTitle}`,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(150)}`,
                        justifyContent: "center",
                    },
                },
            },
            {
                key: 'baseSize',
                sortKey: hideSorting ? undefined : 'baseCount',
                head: 'Base',
                headText: 'Base',
                Value: ({ index }) => <>{getFormattedThousand(data[index]?.baseCount)}</>,
                valueText: (index) => `${data[index]?.baseCount}`,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(100)}`,
                        justifyContent: 'center',
                    },
                },
            },
            ...metrics.map((metric, metricIndex): TableColumn => ({
                key: `${metric.name}`,
                head: metric.name,
                Value: () => null,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(2 * COLUMN_WIDTH)}`,
                        justifyContent: 'center',
                    },
                },
                accentLevel: 2 + (metricIndex + 1) % 2,
                subColumns: [
                    {
                        key: `${metric.name} value`,
                        sortKey: hideSorting ? undefined : `metric${metricIndex}`,
                        head: '%',
                        headText: `${metric.name} %`,
                        Value: ({ index }) => <>{data[index].metrics[metricIndex].value}</>,
                        valueText: (index) => data[index].metrics[metricIndex].value,
                        style: {
                            cell: {
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 1 + (metricIndex + 1) % 2,
                    },
                    {
                        key: `${metric.name} change`,
                        sortKey: hideSorting ? undefined : `metric${metricIndex}Change`,
                        head: 'chg',
                        headText: `${metric.name} chg`,
                        Value: ({ index }) => <>
                            {data[index].metrics[metricIndex].changeValueSigned}
                        </>,
                        valueText: (index) => data[index].metrics[metricIndex].changeValue,
                        style: {
                            cell: {
                                justifyContent: 'center',
                            },
                        },
                        accentLevel: 1 + (metricIndex + 1) % 2,
                    },
                ],
            })),
            {
                key: 'heatScore',
                sortKey: hideSorting ? undefined : 'heatScoreNum',
                head: <span style={{ position: 'relative' }}>
                    Heat Score
                    <AiOutlineInfoCircle
                        data-tooltip-id={heatScoreTooltipId}
                        className="TrendSummary__info-icon"
                        data-testid="buttonTrendSummaryInfoIcon"
                    />
                </span>,
                headText: 'Heat Score',
                style: {
                    cell: {
                        flex: `0 0 ${getRem(150)}`,
                        justifyContent: 'center',
                    },
                },
                Value: ({ index }) => data[index]?.heatScore ? (
                    <HeatScoreLabel
                        heatScoreId={data[index]?.heatScore ?? HeatScore.NoData}
                        modifiers={["medium"]}
                    />
                ) : <>-</>,
                valueText: (index) => data[index]?.heatScoreTitle ?? '',
            },
        ],
        [data, search, hideSorting, heatScoreTooltipId, metrics]
    )
}